import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { compact } from 'lodash-es';
import { notEmpty } from '~/infra/utils';
import { fromDatestampObjectToExpirationDate } from './expiration-date';
import * as serviceModel from './service';
import { formatDateLong, fromDateStampObject } from './time';
import { approvalMethodNoticePeriodModel, introVideoModel } from '.';
export var DraftStatusType = /*#__PURE__*/ function(DraftStatusType) {
    DraftStatusType["Active"] = "active";
    DraftStatusType["Scheduled"] = "scheduled";
    DraftStatusType["Archived"] = "archived";
    DraftStatusType["None"] = "none";
    return DraftStatusType;
}({});
export class DraftModel {
    static fromDTO(dto) {
        return new DraftModel(_object_spread_props(_object_spread({}, dto), {
            legalTermsDocuments: dto.legalTermsDocuments,
            metadata: dto.metadata,
            services: compact(dto.services.map(serviceModel.fromDTO)),
            serviceBundles: dto.serviceBundles ? {
                bundles: dto.serviceBundles.bundles.map((bundle)=>_object_spread_props(_object_spread({}, bundle), {
                        services: compact(bundle.services.map(serviceModel.fromDTO))
                    }))
            } : undefined
        }));
    }
    static fromRelationship(relationship) {
        return new DraftModel({
            id: relationship.id,
            coverLetter: relationship.customNotificationText,
            extraClause: relationship.extraClause || '',
            requirePaymentMethod: relationship.requirePaymentMethod != null ? relationship.requirePaymentMethod : true,
            allowManualPayments: relationship.allowManualPayments,
            isExistingCustomer: false,
            customNotificationText: relationship.customNotificationText,
            title: relationship.title,
            lastModifiedOn: 0,
            lastModifiedBy: null,
            services: relationship.services,
            serviceBundles: relationship.serviceBundles,
            netTerms: relationship.netTerms,
            effectiveDate: relationship.effectiveDate,
            feeControls: relationship.feeControls,
            clientContactId: relationship.clientContactId,
            isSample: relationship.isSample,
            amendmentsApprovalMethod: relationship.amendmentsApprovalMethod,
            invoicesApprovalMethod: relationship.invoicesApprovalMethod,
            requestAccountantAccess: relationship.requestAccountantAccess,
            introVideo: relationship.introVideo,
            expirationDate: fromDatestampObjectToExpirationDate(relationship.expirationDate, relationship.createdOn),
            legalTermsDocuments: relationship.legalTermsDocuments,
            reviewers: [
                ...(relationship.signatories || []).map((s)=>({
                        firstName: s.firstName || undefined,
                        lastName: s.lastName || undefined,
                        email: s.email,
                        isSignatory: true
                    })),
                ...relationship.reviewers.map((r)=>({
                        firstName: r.firstName || undefined,
                        lastName: r.lastName || undefined,
                        email: r.email,
                        isSignatory: false
                    }))
            ],
            metadata: undefined,
            customPages: relationship.customPages,
            status: {
                type: "active"
            }
        });
    }
    toDTO() {
        return _object_spread_props(_object_spread({}, this.data), {
            services: this.data.services.map(serviceModel.toDTO),
            legalTermsDocuments: this.data.legalTermsDocuments.filter(notEmpty),
            serviceBundles: this.data.serviceBundles != null ? {
                bundles: this.data.serviceBundles.bundles.map((bundle)=>_object_spread_props(_object_spread({}, bundle), {
                        services: bundle.services.map(serviceModel.toDTO)
                    }))
            } : undefined
        });
    }
    id() {
        return this.data.id;
    }
    coverLetter() {
        return this.data.coverLetter;
    }
    extraClause() {
        return this.data.extraClause;
    }
    requirePaymentMethod() {
        return this.data.requirePaymentMethod;
    }
    allowWithoutPaymentMethod() {
        return !this.data.requirePaymentMethod;
    }
    allowManualPayments() {
        return this.data.allowManualPayments;
    }
    isExistingCustomer() {
        return this.data.isExistingCustomer;
    }
    customNotificationText() {
        return this.data.customNotificationText;
    }
    title() {
        return this.data.title;
    }
    lastModifiedOn() {
        return this.data.lastModifiedOn;
    }
    lastModifiedBy() {
        return this.data.lastModifiedBy;
    }
    services() {
        return this.data.services;
    }
    serviceBundles() {
        return this.data.serviceBundles ? this.data.serviceBundles.bundles : [];
    }
    netTerms() {
        return this.data.netTerms;
    }
    effectiveDate() {
        return this.data.effectiveDate;
    }
    effectiveDateAsDate() {
        return this.data.effectiveDate ? fromDateStampObject(this.data.effectiveDate) : null;
    }
    formattedEffectiveDate() {
        return this.data.effectiveDate ? formatDateLong(this.data.effectiveDate) : null;
    }
    legalTermsDocuments() {
        return this.data.legalTermsDocuments;
    }
    coverCreditCardFees() {
        return this.data.feeControls.coverCreditCardFees;
    }
    clientContactId() {
        return this.data.clientContactId;
    }
    isSample() {
        return this.data.isSample;
    }
    amendmentsApprovalMethod() {
        return this.data.amendmentsApprovalMethod;
    }
    invoicesApprovalMethod() {
        return this.data.invoicesApprovalMethod;
    }
    requestAccountantAccess() {
        return this.data.requestAccountantAccess;
    }
    reviewers() {
        return this.data.reviewers;
    }
    introVideo() {
        return this.data.introVideo;
    }
    introVideoLink() {
        return this.data.introVideo ? introVideoModel.videoUrl(this.data.introVideo) : null;
    }
    expirationDate() {
        return this.data.expirationDate;
    }
    linkedRelationshipId() {
        var _this_data_linkedRelationshipId;
        return (_this_data_linkedRelationshipId = this.data.linkedRelationshipId) !== null && _this_data_linkedRelationshipId !== void 0 ? _this_data_linkedRelationshipId : null;
    }
    metadata() {
        return this.data.metadata;
    }
    templateVariableValues() {
        return this.data.templateVariableValues;
    }
    customPages() {
        var _this_data_customPages;
        return (_this_data_customPages = this.data.customPages) !== null && _this_data_customPages !== void 0 ? _this_data_customPages : [];
    }
    setCustomPages(pages) {
        return this.setChanges({
            customPages: pages
        });
    }
    service(serviceId) {
        if (this.data.serviceBundles) {
            return this.data.serviceBundles.bundles.flatMap((b)=>b.services).find((s)=>s.id === serviceId);
        }
        return this.data.services.find((s)=>s.id === serviceId);
    }
    hasNoServicesOrBundles() {
        return !this.data.serviceBundles && !this.data.services.length;
    }
    expirationDaysAfterSending() {
        var _this_data_expirationDate;
        return ((_this_data_expirationDate = this.data.expirationDate) === null || _this_data_expirationDate === void 0 ? void 0 : _this_data_expirationDate.type) === 'days' ? this.data.expirationDate.days : null;
    }
    defaultExpirationDays(worksAt) {
        var _worksAt_settings_expirationDate, _worksAt_settings;
        return (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings = worksAt.settings) === null || _worksAt_settings === void 0 ? void 0 : (_worksAt_settings_expirationDate = _worksAt_settings.expirationDate) === null || _worksAt_settings_expirationDate === void 0 ? void 0 : _worksAt_settings_expirationDate.type) === 'days' ? worksAt.settings.expirationDate.days : 30;
    }
    defaultAutoApprovalMethod(worksAt) {
        var _worksAt_settings;
        return (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings = worksAt.settings) === null || _worksAt_settings === void 0 ? void 0 : _worksAt_settings.amendmentsApprovalMethod) && approvalMethodNoticePeriodModel.isAutoApproval(worksAt.settings.amendmentsApprovalMethod) && worksAt.settings.amendmentsApprovalMethod.type === 'auto' ? worksAt.settings.amendmentsApprovalMethod.auto.noticePeriodDays : 30;
    }
    defaultAutoApproveOutOfScopeCharges(worksAt) {
        var _worksAt_settings;
        return (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings = worksAt.settings) === null || _worksAt_settings === void 0 ? void 0 : _worksAt_settings.invoicesApprovalMethod) && approvalMethodNoticePeriodModel.isAutoApproval(worksAt.settings.invoicesApprovalMethod) && worksAt.settings.invoicesApprovalMethod.type === 'auto' ? worksAt.settings.invoicesApprovalMethod.auto.noticePeriodDays : 30;
    }
    isScheduled() {
        return this.data.status.type === "scheduled";
    }
    scheduledDate() {
        return this.data.status.type === "scheduled" ? this.data.status.schedule.time : null;
    }
    addServices(services, packageId) {
        if (packageId) {
            if (this.data.serviceBundles) {
                const newBundles = this.data.serviceBundles.bundles.map((b)=>{
                    if (b.id === packageId) {
                        return _object_spread_props(_object_spread({}, b), {
                            services: b.services.concat(services)
                        });
                    }
                    return b;
                });
                return this.setServiceBundles(newBundles);
            } else {
                return this;
            }
        } else {
            return this.setServices(this.data.services.concat(services));
        }
    }
    setServices(services) {
        return this.setChanges({
            services
        });
    }
    setServiceBundles(bundles) {
        return this.setChanges({
            serviceBundles: bundles.length > 0 ? {
                bundles
            } : undefined
        });
    }
    setService(serviceId, transformer) {
        if (this.data.serviceBundles) {
            const newBundles = this.data.serviceBundles.bundles.map((b)=>_object_spread_props(_object_spread({}, b), {
                    services: b.services.map((s)=>s.id === serviceId ? transformer(s) : s)
                }));
            return this.setChanges({
                serviceBundles: {
                    bundles: newBundles
                }
            });
        } else {
            return this.setChanges({
                services: this.data.services.map((s)=>s.id === serviceId ? transformer(s) : s)
            });
        }
    }
    removeService(serviceId) {
        if (this.data.serviceBundles) {
            return this.setChanges({
                serviceBundles: {
                    bundles: this.data.serviceBundles.bundles.map((bundle)=>_object_spread_props(_object_spread({}, bundle), {
                            services: bundle.services.filter((s)=>s.id !== serviceId)
                        }))
                }
            });
        }
        return this.setChanges({
            services: this.data.services.filter((s)=>s.id !== serviceId)
        });
    }
    setPackageName(packageId, packageName) {
        if (!this.data.serviceBundles) {
            return this;
        }
        return this.setChanges({
            serviceBundles: {
                bundles: this.data.serviceBundles.bundles.map((bundle)=>bundle.id === packageId ? _object_spread_props(_object_spread({}, bundle), {
                        name: packageName
                    }) : bundle)
            }
        });
    }
    setLegalTermsDocuments(legalTermsDocuments) {
        return this.setChanges({
            legalTermsDocuments
        });
    }
    setRequirePaymentMethod(requirePaymentMethod) {
        return this.setChanges({
            requirePaymentMethod
        });
    }
    setNetTerms(netTerms) {
        return this.setChanges({
            netTerms
        });
    }
    setAllowManualPayments(allowManualPayments) {
        return this.setChanges({
            allowManualPayments
        });
    }
    setIntroVideo(introVideo) {
        return this.setChanges({
            introVideo
        });
    }
    setCustomNotificationText(customNotificationText) {
        return this.setChanges({
            customNotificationText
        });
    }
    setExpirationDate(expirationDate) {
        return this.setChanges({
            expirationDate
        });
    }
    setEffectiveDate(effectiveDate) {
        return this.setChanges({
            effectiveDate
        });
    }
    setCoverCreditCardFees(coverCreditCardFees) {
        return this.setChanges({
            feeControls: _object_spread_props(_object_spread({}, this.data.feeControls), {
                coverCreditCardFees
            })
        });
    }
    setSampleContact() {
        return this.setChanges({
            clientContactId: null,
            isSample: true
        });
    }
    setClientContactId(clientContactId) {
        return this.setChanges({
            clientContactId,
            isSample: false
        });
    }
    setInvoicesApprovalMethod(invoicesApprovalMethod) {
        return this.setChanges({
            invoicesApprovalMethod
        });
    }
    setAmendmentsApprovalMethod(amendmentsApprovalMethod) {
        return this.setChanges({
            amendmentsApprovalMethod
        });
    }
    setExtraClause(extraClause) {
        return this.setChanges({
            extraClause
        });
    }
    setTitle(title) {
        return this.setChanges({
            title
        });
    }
    setReviewers(reviewers) {
        return this.setChanges({
            reviewers
        });
    }
    setLastModifiedOn(lastModifiedOn) {
        return this.setChanges({
            lastModifiedOn
        });
    }
    setId(id) {
        return this.setChanges({
            id
        });
    }
    setChanges(changes) {
        return new DraftModel(_object_spread_props(_object_spread({}, this.data, changes), {
            id: changes.id || this.data.id
        }));
    }
    constructor(data){
        _define_property(this, "data", void 0);
        this.data = data;
    }
}
